import { useEffect, useRef, useState } from 'react';

export interface ComposerProps {
  disabled?: boolean;
  onSend: (text: string) => void;
  /** Item 268 reverso — disparado quando cliente começa a digitar. */
  onTypingStart: () => void;
  /** Item 268 reverso — disparado quando cliente para de digitar (debounce). */
  onTypingStop: () => void;
}

const TYPING_DEBOUNCE_MS = 1500;

export function Composer({
  disabled = false,
  onSend,
  onTypingStart,
  onTypingStop,
}: ComposerProps): JSX.Element {
  const [text, setText] = useState('');
  const typingActiveRef = useRef(false);
  const debounceRef = useRef<number | null>(null);

  function fireTypingStart(): void {
    if (typingActiveRef.current) return;
    typingActiveRef.current = true;
    onTypingStart();
  }

  function fireTypingStop(): void {
    if (!typingActiveRef.current) return;
    typingActiveRef.current = false;
    onTypingStop();
  }

  function scheduleTypingStop(): void {
    if (debounceRef.current !== null) {
      window.clearTimeout(debounceRef.current);
    }
    debounceRef.current = window.setTimeout(() => {
      fireTypingStop();
      debounceRef.current = null;
    }, TYPING_DEBOUNCE_MS);
  }

  useEffect(() => {
    return () => {
      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current);
      }
    };
  }, []);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const value = e.target.value;
    setText(value);
    if (value.length > 0) {
      fireTypingStart();
      scheduleTypingStop();
    } else {
      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      fireTypingStop();
    }
  }

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (text.trim().length === 0 || disabled) return;
    if (debounceRef.current !== null) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    fireTypingStop();
    onSend(text);
    setText('');
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        display: 'flex',
        gap: '8px',
        padding: '12px 16px',
        borderTop: '1px solid var(--lume-border)',
        background: 'var(--lume-panel)',
      }}
    >
      <input
        type="text"
        value={text}
        onChange={handleChange}
        placeholder={disabled ? 'Conectando…' : 'Digite sua mensagem…'}
        disabled={disabled}
        aria-label="Mensagem"
        style={{
          flex: 1,
          padding: '10px 12px',
          border: '1px solid var(--lume-border)',
          borderRadius: '8px',
          fontSize: '14px',
          outline: 'none',
          background: disabled ? 'var(--lume-bg)' : 'var(--lume-panel)',
        }}
      />
      <button
        type="submit"
        disabled={disabled || text.trim().length === 0}
        style={{
          padding: '10px 16px',
          border: 'none',
          borderRadius: '8px',
          background: 'var(--lume-accent)',
          color: '#ffffff',
          fontWeight: 600,
          fontSize: '14px',
          cursor: disabled || text.trim().length === 0 ? 'not-allowed' : 'pointer',
          opacity: disabled || text.trim().length === 0 ? 0.5 : 1,
        }}
      >
        Enviar
      </button>
    </form>
  );
}
